import React, { useState } from 'react';

import {
  faBell,
  faBellSlash,
  faSquareCaretDown,
  faSquareCaretUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useParams, useNavigate, Link } from 'react-router-dom';

import type { Caller } from '../components/caller/types';
import EncounterList from '../components/encounter/EncounterList';
import ErrorState from '../components/state/ErrorState';
import LoadingState from '../components/state/LoadingState';
import { FormatPhone } from '../utils/format';
import { usePostJSON } from '../utils/request';
import { useTitle } from '../utils/title';

interface CallerResponse {
  success: boolean;
  caller: Caller;
}

const CallerEncounters: React.FC = () => {
  const { id: _id } = useParams<{ id: string }>();
  const id = _id ? parseInt(_id, 10) : undefined;
  const navigate = useNavigate();
  const [callerInfoCollapsed, setCallerInfoCollapsed] = useState(true);

  const { data: callerResponse, error, loading } = usePostJSON<CallerResponse>(
    '/api/caller/data_one',
    { id },
    [id]
  );

  const caller = callerResponse?.caller || null;
  const callerName = caller
    ? `${caller.firstName || ''} ${caller.lastName || ''}`.trim() || 'Unknown Caller'
    : '';
  useTitle(callerName ? `${callerName} - Encounters` : 'Caller Encounters');

  if (!id) {
    return (
      <Stack gap="md">
        <Title order={1}>Caller Encounters</Title>
        <Paper withBorder p="md">
          <ErrorState
            message="Caller ID is required"
            style={{ minHeight: '200px' }}
            title="Invalid Caller ID"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/callers')}>
              Back to Callers
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (loading) {
    return (
      <LoadingState
        message="Loading caller data..."
        style={{ height: '50vh' }}
      />
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Title order={1}>Caller Encounters</Title>
        <Paper withBorder p="md">
          <ErrorState
            message={error.message}
            style={{ minHeight: '200px' }}
            title="Error Loading Caller"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/callers')}>
              Back to Callers
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (!caller) {
    return (
      <Stack gap="md">
        <Title order={1}>Caller Encounters</Title>
        <Paper withBorder p="md">
          <Alert color="red" title="Caller Not Found">
            The requested caller could not be found.
          </Alert>
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/callers')}>
              Back to Callers
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group align="center" justify="space-between">
        <Button variant="outline" onClick={() => void navigate('/callers')}>
          Back to All Callers
        </Button>
        <Button
          component={Link}
          to={`/caller/edit/${caller.id}`}
          variant="outline"
        >
          Edit Caller
        </Button>
      </Group>

      <div style={{ marginTop: '0.5rem' }} />

      <Paper withBorder p="md" shadow="sm">
        <Stack gap="md">
          {callerInfoCollapsed ? (
            <Group align="center" justify="space-between">
              <Group gap="sm">
                <Text fw={600} size="xl">
                  {callerName}
                </Text>
                {caller.type && (
                  <Badge
                    color={
                      caller.type.toLowerCase() === 'aide'
                        ? 'blue'
                        : caller.type.toLowerCase() === 'family'
                        ? 'green'
                        : 'gray'
                    }
                    variant="light"
                  >
                    {caller.type}
                  </Badge>
                )}
                {caller.isOptedOut && (
                  <Badge
                    color="orange"
                    leftSection={<FontAwesomeIcon icon={faBellSlash} size="xs" />}
                    variant="light"
                  >
                    Opted out
                  </Badge>
                )}
                {caller.configs?.visitUpdatesEnabled && (
                  <Badge
                    color="teal"
                    leftSection={<FontAwesomeIcon icon={faBell} size="xs" />}
                    variant="light"
                  >
                    Visit Updates
                  </Badge>
                )}
              </Group>
              <ActionIcon
                variant="subtle"
                onClick={() => setCallerInfoCollapsed(false)}
              >
                <FontAwesomeIcon icon={faSquareCaretDown} size="sm" />
              </ActionIcon>
            </Group>
          ) : (
            <>
              <Group align="center" justify="space-between">
                <Title order={2} size="h3">Caller Information</Title>
                <ActionIcon
                  variant="subtle"
                  onClick={() => setCallerInfoCollapsed(true)}
                >
                  <FontAwesomeIcon icon={faSquareCaretUp} size="sm" />
                </ActionIcon>
              </Group>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Name</Text>
                    <Text fw={600} size="lg">{callerName}</Text>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Phone</Text>
                    <Text size="md">{FormatPhone(caller.phone)}</Text>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Email</Text>
                    <Text size="md">
                      {caller.email || <Text c="dimmed" component="span" size="lg">NOT PROVIDED</Text>}
                    </Text>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Type</Text>
                    {caller.type ? (
                      <Badge
                        color={
                          caller.type.toLowerCase() === 'aide'
                            ? 'blue'
                            : caller.type.toLowerCase() === 'family'
                            ? 'green'
                            : 'gray'
                        }
                        size="lg"
                        variant="light"
                      >
                        {caller.type}
                      </Badge>
                    ) : (
                      <Text c="dimmed" size="lg">NOT SET</Text>
                    )}
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Opted Out</Text>
                    <Text size="md">
                      {caller.isOptedOut ? (
                        <Badge color="orange" variant="light">Yes</Badge>
                      ) : (
                        'No'
                      )}
                    </Text>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Visit Updates</Text>
                    <Text size="md">
                      {caller.configs?.visitUpdatesEnabled ? (
                        <Badge color="teal" variant="light">Enabled</Badge>
                      ) : caller.configs?.visitUpdatesEnabled === false ? (
                        'Declined'
                      ) : (
                        <Text c="dimmed" component="span" size="md">Not asked</Text>
                      )}
                    </Text>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Text c="dimmed" fw={500} size="sm">Created</Text>
                    <Text c="dimmed" size="md">
                      {new Date(caller.createdAt).toLocaleDateString()}
                    </Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </>
          )}
        </Stack>
      </Paper>

      <div style={{ marginTop: '0.5rem' }} />

      <EncounterList
        callerId={id}
        title={`Encounters for ${callerName}`}
      />
    </Stack>
  );
};

export default CallerEncounters;
